// Chat's device-local storage (chat-mls.md §6, §8.1): IndexedDB holds the
// MLS session blob (signature secrets + group state — as sensitive as the
// conversation) and the PLAINTEXT history. Both live only on this device by
// the owner's decision: forward secrecy makes old ciphertext undecryptable,
// so the device's own copy IS the history. CSP (§9.2) is the web's
// protection wall; SECURITY.md states the posture.

import type { FileRef } from './wire';

const DB_VERSION = 1;

export interface ConvMeta {
  id: string;
  kind: 'dm' | 'group';
  peerUserId?: string;
  /** MLS group id (hex), known once created or joined. */
  groupId?: string;
  joined: boolean;
  /** Seq of the welcome we joined through; 0 for the creator. */
  joinSeq: number;
  /** Our view of the conversation's MLS epoch (commit ordering). */
  epoch: number;
  /** Last envelope seq this device has processed (drives replay + ack). */
  lastProcessedSeq: number;
  /** Last seq the person has SEEN (drives unread). */
  lastReadSeq: number;
  /** We were removed; the thread stays readable, sending is off. */
  removed?: boolean;
  /** WE started this conversation (owner feedback: the other person should
   * see nothing until a first message actually exists). */
  initiatedByMe?: boolean;
}

export interface StoredMessage {
  convId: string;
  /** Envelope seq for received messages; own sends use their assigned seq. */
  seq: number;
  isMine: boolean;
  senderDevice: string;
  text: string;
  at: number; // epoch ms
  /** emoji → reacting USER ids (per-person, so my two devices count once). */
  reactions?: Record<string, string[]>;
  /** A shared file, when this message is one (the text holds the filename). */
  file?: FileRef;
}

function open(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('kv');
      db.createObjectStore('convs', { keyPath: 'id' });
      const messages = db.createObjectStore('messages', { keyPath: ['convId', 'seq'] });
      messages.createIndex('byConv', 'convId');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;
let dbUser = '';

/** Select the signed-in user's database. Chat identity, state and history
 * are PER ACCOUNT — two accounts in one browser must never share a device
 * (that mix-up produced silent 403s on every send). */
export function selectDatabase(userId: string): void {
  if (userId === dbUser) return;
  dbUser = userId;
  dbPromise = null;
}

function db(): Promise<IDBDatabase> {
  if (!dbUser) throw new Error('chatDb.useDatabase must run first');
  // v2: local namespace bumped for the staging reset (2026-08-26) —
  // old browser data from the buggy first round is deliberately orphaned.
  dbPromise ??= open(`bz-chat-v4-${dbUser}`);
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(store, mode);
        const req = run(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
      }),
  );
}

export const chatDb = {
  getKV: <T>(key: string) => tx<T | undefined>('kv', 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>),
  putKV: (key: string, value: unknown) => tx('kv', 'readwrite', (s) => s.put(value, key)),

  getConv: (id: string) =>
    tx<ConvMeta | undefined>('convs', 'readonly', (s) => s.get(id) as IDBRequest<ConvMeta | undefined>),
  putConv: (meta: ConvMeta) => tx('convs', 'readwrite', (s) => s.put(meta)),
  allConvs: () => tx<ConvMeta[]>('convs', 'readonly', (s) => s.getAll() as IDBRequest<ConvMeta[]>),

  putMessage: (m: StoredMessage) => tx('messages', 'readwrite', (s) => s.put(m)),
  getMessage: (convId: string, seq: number) =>
    tx<StoredMessage | undefined>('messages', 'readonly', (s) => s.get([convId, seq]) as IDBRequest<StoredMessage | undefined>),
  deleteConv: (id: string) => tx('convs', 'readwrite', (s) => s.delete(id)),
  deleteMessagesOf: (convId: string) =>
    db().then(
      (d) =>
        new Promise<void>((resolve, reject) => {
          const t = d.transaction('messages', 'readwrite');
          const req = t.objectStore('messages').index('byConv').openKeyCursor(convId);
          req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor) return resolve();
            t.objectStore('messages').delete(cursor.primaryKey);
            cursor.continue();
          };
          req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
        }),
    ),
  messagesOf: (convId: string) =>
    db().then(
      (d) =>
        new Promise<StoredMessage[]>((resolve, reject) => {
          const t = d.transaction('messages', 'readonly');
          const req = t.objectStore('messages').index('byConv').getAll(convId);
          req.onsuccess = () => resolve((req.result as StoredMessage[]).sort((a, b) => a.seq - b.seq));
          req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
        }),
    ),
};
